import React, { useMemo, useState } from 'react';
import { Star, ShoppingBag, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

function ProductCard({ p }) {
  const [size, setSize] = useState(p.sizes[1] || p.sizes[0]);
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user, login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem(p, size);
    toast({ title: 'Added to cart', description: `${p.name} · ${size}` });
  };

  const handleWishlist = async (e) => {
    e.stopPropagation();
    if (!user) { toast({ title: 'Sign in first', description: 'Sign in to save your favourite spices.' }); return login(); }
    const r = await toggle(p.id);
    if (r.ok) toast({ title: r.has ? 'Added to wishlist' : 'Removed from wishlist' });
  };

  const goToProduct = () => navigate(`/product/${p.id}`);

  return (
    <div className="product-card p-4 flex flex-col cursor-pointer" onClick={goToProduct}>
      {p.badge && (
        <span className="chip absolute top-3 left-3 z-10" style={{ background: 'var(--gk-terracotta)', color: '#fff' }}>{p.badge}</span>
      )}
      <button onClick={handleWishlist} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-colors" style={{ background: 'rgba(255,255,255,0.9)' }} aria-label="Wishlist">
        <Heart size={14} fill={has(p.id) ? 'var(--gk-terracotta)' : 'none'} style={{ color: 'var(--gk-terracotta)' }} />
      </button>
      <div className="aspect-square rounded-lg overflow-hidden mb-3" style={{ background: 'var(--gk-cream-dark)' }}>
        <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      </div>
      <h3 className="font-serif text-[15px] md:text-base font-semibold leading-snug min-h-[48px]" style={{ color: 'var(--gk-ink)' }}>{p.name}</h3>
      <div className="flex items-center gap-1 mt-2 mb-2">
        {[0,1,2,3,4].map(i => <Star key={i} size={13} fill="var(--gk-gold)" strokeWidth={0} />)}
        <span className="text-xs ml-1" style={{ color: 'var(--gk-muted)' }}>({p.reviews})</span>
      </div>
      <div className="font-bold text-lg" style={{ color: 'var(--gk-green-dark)' }}>₹{p.price}</div>
      <div className="flex flex-wrap gap-1.5 mt-3" onClick={e => e.stopPropagation()}>
        {p.sizes.map(s => (
          <button key={s} onClick={() => setSize(s)} className="text-xs px-2.5 py-1 rounded-full transition-colors"
            style={size === s
              ? { background: 'var(--gk-green)', color: '#fff', border: '1px solid var(--gk-green)' }
              : { background: 'transparent', color: 'var(--gk-ink)', border: '1px solid #ddd0b3' }}>
            {s}
          </button>
        ))}
      </div>
      <button onClick={handleAdd} className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full transition-colors"
        style={{ background: 'var(--gk-cream-dark)', color: 'var(--gk-green-dark)' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--gk-green)'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--gk-cream-dark)'; e.currentTarget.style.color = 'var(--gk-green-dark)'; }}>
        <ShoppingBag size={14} /> Add to Cart
      </button>
    </div>
  );
}

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'whole', label: 'Whole Spices' },
  { key: 'ground', label: 'Ground Spices' },
  { key: 'blends', label: 'Masala Blends' },
];

export default function BestSellingSpices({ defaultCategory = 'all', hideTitle = false }) {
  const { products, loading } = useProducts();
  const [category, setCategory] = useState(defaultCategory);
  const [visible, setVisible] = useState(10);

  const filtered = useMemo(() => {
    if (category === 'all') return products;
    return products.filter(p => p.category === category);
  }, [products, category]);

  return (
    <section id="all" className="py-16 md:py-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {!hideTitle && (
          <div className="text-center mb-8">
            <div className="divider-leaf max-w-md mx-auto mb-4 text-xs tracking-[0.3em] uppercase font-semibold">• Bestsellers •</div>
            <h2 className="section-title text-3xl md:text-4xl">Best Selling Spices</h2>
            <p className="mt-3 text-sm md:text-base max-w-2xl mx-auto" style={{ color: 'var(--gk-muted)' }}>Fresh, small-batch spices milled and packed on order — the very same ones our family kitchens have loved for generations.</p>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {CATEGORIES.map(c => (
            <button key={c.key} onClick={() => { setCategory(c.key); setVisible(10); }}
              className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
              style={category === c.key
                ? { background: 'var(--gk-green)', color: '#fff', border: '1px solid var(--gk-green)' }
                : { background: '#fff', color: 'var(--gk-ink)', border: '1px solid #ddd0b3' }}>
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="product-card p-4 animate-pulse">
                <div className="aspect-square rounded-lg mb-3" style={{ background: 'var(--gk-cream-dark)' }} />
                <div className="h-4 rounded w-3/4" style={{ background: 'var(--gk-cream-dark)' }} />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-10" style={{ color: 'var(--gk-muted)' }}>No spices in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {filtered.slice(0, visible).map(p => <ProductCard key={p.id} p={p} />)}
          </div>
        )}

        {!loading && visible < filtered.length && (
          <div className="text-center mt-10">
            <button className="gk-btn-outline" onClick={() => setVisible(filtered.length)}>View All Spices</button>
          </div>
        )}
      </div>
    </section>
  );
}
